import { useEffect, useState } from "react"
import type { POI } from "@/types/explore"
import { getNearbyPOIs, getCommuneHistoricMonuments } from "@/lib/explore/api/geo"

const poisCache = new Map<string, POI[]>()

export function usePois(center: [number, number] | null, codeInsee: string | null, codeDep: string | null) {
  const [pois, setPois] = useState<POI[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  const lon = center?.[0] ?? null
  const lat = center?.[1] ?? null

  useEffect(() => {
    if (lon === null || lat === null) {
      setPois([])
      setLoading(false)
      setError(false)
      return
    }

    const cacheKey = `${lon},${lat}`
    const cached = poisCache.get(cacheKey)
    if (cached) {
      setPois(cached)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(false)

    const promises: Promise<POI[]>[] = [getNearbyPOIs(lat, lon)]
    if (codeInsee && codeDep) {
      promises.push(getCommuneHistoricMonuments(codeInsee, codeDep, lat, lon))
    }

    Promise.all(promises)
      .then((results) => {
        const sorted = results
          .flat()
          .filter((poi) => poi.distance != null && poi.distance < 1000)
          .sort((a, b) => (a.distance ?? 0) - (b.distance ?? 0))
        poisCache.set(cacheKey, sorted)
        setPois(sorted)
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
        setError(true)
      })
  }, [lon, lat, codeInsee, codeDep])

  return { pois, loading, error }
}
